import {
  FileText,
  Mail,
  MailOpen,
  MessagesSquare,
  Package,
  Paperclip,
  Sparkles,
} from "lucide-react";

import { SensitiveText } from "@/components/common/Sensitive";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { KaiGraphMailAttachmentsCard } from "./KaiGraphMailAttachmentsCard";
import type {
  KaiMailReviewMessage,
  KaiMailReviewSnapshot,
  KaiMailReviewThread,
} from "./kaiMailReviewTypes";
import type { KaiMailReviewContext } from "@/services/kaiApi";

/** Humanise a classification category slug (e.g. `pre_alert` → "Pre alert"). */
function categoryLabel(slug: string): string {
  return slug
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** First token of an email address / display name, for a compact sender chip. */
function senderShort(from: string | null | undefined): string {
  if (!from) return "Unknown sender";
  const trimmed = from.trim();
  const angle = trimmed.match(/^"?([^"<]+)"?\s*</);
  if (angle && angle[1].trim()) return angle[1].trim();
  return trimmed;
}

/** Build the drill-down context the follow-up turn carries. */
function messageContext(
  thread: KaiMailReviewThread,
  msg: KaiMailReviewMessage,
): KaiMailReviewContext {
  return {
    message_id: msg.message_id,
    mailbox_id: msg.mailbox_id ?? null,
    conversation_id: thread.conversation_id ?? null,
    booking_source_id: msg.booking_source_id ?? null,
    subject: msg.subject ?? thread.subject ?? null,
    from_address: msg.from_address ?? null,
  };
}

function rowActionClassName() {
  return cn(
    "inline-flex items-center gap-1 text-[11px] font-medium text-primary/90",
    "transition-colors hover:text-primary hover:underline",
  );
}

function MessageRow({
  thread,
  msg,
  onReviewEmail,
  variant,
}: {
  thread: KaiMailReviewThread;
  msg: KaiMailReviewMessage;
  onReviewEmail?: (ctx: KaiMailReviewContext, text: string) => void;
  /** Single-message thread: subject is the section title, not repeated below. */
  variant: "standalone" | "in-thread";
}) {
  const { formatDate } = useDateFormatter();
  const ctx = messageContext(thread, msg);
  const threadTitle = thread.subject?.trim() || "(no subject)";
  const messageSubject = (msg.subject ?? thread.subject)?.trim();
  const showMessageSubject =
    variant === "in-thread" &&
    Boolean(messageSubject && messageSubject !== threadTitle);
  const standaloneDate = msg.received_at ?? thread.latest_received_at;

  return (
    <li
      className={cn(
        "flex min-w-0 flex-col gap-1.5",
        variant === "in-thread" &&
          "border-t border-border/35 pt-2.5 first:border-t-0 first:pt-0",
      )}
    >
      {variant === "standalone" && (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <MailOpen className="size-4 shrink-0 text-primary" />
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
            <SensitiveText value={threadTitle} />
          </p>
          {standaloneDate && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatDate(standaloneDate)}
            </span>
          )}
        </div>
      )}

      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        {variant === "in-thread" && (
          <Mail className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 truncate text-sm font-medium text-foreground">
          <SensitiveText value={senderShort(msg.from_address)} />
        </span>
        {variant === "in-thread" && msg.received_at && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatDate(msg.received_at)}
          </span>
        )}
        {msg.category && (
          <Badge variant="info" className="shrink-0 px-2 py-0 text-[10px]">
            {categoryLabel(msg.category)}
          </Badge>
        )}
        {msg.has_attachments && (
          <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
        )}
      </div>

      {showMessageSubject && (
        <p className="min-w-0 truncate text-sm text-foreground/90">
          <SensitiveText value={messageSubject} />
        </p>
      )}

      {msg.digest && (
        <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
          {msg.digest}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {msg.booking_source_id && (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <Package className="size-3" />
            Order {msg.booking_source_id}
          </span>
        )}
        {msg.file_id && (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <FileText className="size-3" />
            File {msg.file_id}
          </span>
        )}
      </div>

      {onReviewEmail && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5">
          <button
            type="button"
            onClick={() =>
              onReviewEmail(ctx, "Explain this email in booking context.")
            }
            className={rowActionClassName()}
          >
            <Sparkles className="size-3" />
            Explain this email
          </button>
          <button
            type="button"
            onClick={() => onReviewEmail(ctx, "Show the full thread for this email.")}
            className={rowActionClassName()}
          >
            <MessagesSquare className="size-3" />
            Show full thread
          </button>
        </div>
      )}
    </li>
  );
}

function ThreadSection({
  thread,
  onReviewEmail,
}: {
  thread: KaiMailReviewThread;
  onReviewEmail?: (ctx: KaiMailReviewContext, text: string) => void;
}) {
  const { formatDate } = useDateFormatter();
  const title = thread.subject?.trim() || "(no subject)";
  const multi = thread.messages.length > 1;

  return (
    <section className="min-w-0 py-3 first:pt-0 last:pb-0">
      {multi && (
        <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2">
          <MailOpen className="size-4 shrink-0 text-primary" />
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
            <SensitiveText value={title} />
          </p>
          {thread.latest_received_at && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatDate(thread.latest_received_at)}
            </span>
          )}
          <Badge variant="secondary" className="shrink-0 px-2 py-0 text-[10px]">
            {thread.message_count} messages
          </Badge>
        </div>
      )}
      <ul className="flex flex-col">
        {thread.messages.map((msg) => (
          <MessageRow
            key={msg.message_id}
            thread={thread}
            msg={msg}
            onReviewEmail={onReviewEmail}
            variant={multi ? "in-thread" : "standalone"}
          />
        ))}
      </ul>
    </section>
  );
}

/** Compact chips describing the filters that produced this review. */
function FilterChips({ spec }: { spec: KaiMailReviewSnapshot }) {
  const f = spec.filters;
  const chips: string[] = [];
  const fromAddress = f.from_address?.trim() || null;
  if (f.to_address) chips.push(`To ${f.to_address}`);
  if (f.source_id) chips.push(`Order ${f.source_id}`);
  if (f.file_id) chips.push(`File ${f.file_id}`);
  if (f.consignee) chips.push(`Consignee ${f.consignee}`);
  if (f.sales_person) chips.push(`Sales ${f.sales_person}`);
  if (f.category) chips.push(categoryLabel(f.category));
  if (f.received_from || f.received_to) {
    const from = f.received_from ? f.received_from.slice(0, 10) : "…";
    const to = f.received_to ? f.received_to.slice(0, 10) : "…";
    chips.push(`${from} → ${to}`);
  }
  if (chips.length === 0 && !fromAddress) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {fromAddress && (
        <Badge variant="outline" className="gap-1 px-2 py-0 text-[10px]">
          From <SensitiveText value={fromAddress} />
        </Badge>
      )}
      {chips.map((c) => (
        <Badge key={c} variant="outline" className="px-2 py-0 text-[10px]">
          {c}
        </Badge>
      ))}
    </div>
  );
}

/**
 * Renders a deterministic `mail_review_snapshot` artifact inline in Kai chat —
 * a scannable, thread-grouped review of Microsoft Graph (Outlook) mail with
 * per-message digests, job chips, and row actions to drill into a single email
 * or its full thread. Bodies are never shown — only the stored digest.
 */
export function KaiMailReviewSnapshot({
  spec,
  onReviewEmail,
}: {
  spec: KaiMailReviewSnapshot;
  onReviewEmail?: (ctx: KaiMailReviewContext, text: string) => void;
}) {
  const title = spec.title?.trim() || "Mail review";
  const threadCount = spec.threads.length;

  return (
    <div className="glass-panel my-2 flex min-w-0 flex-col gap-3 overflow-hidden rounded-xl p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-base font-semibold text-foreground">
          {title}
        </p>
        {threadCount > 0 && (
          <Badge variant="info" className="shrink-0 px-2 py-0.5 text-[10px]">
            {threadCount} {threadCount === 1 ? "thread" : "threads"}
          </Badge>
        )}
      </div>

      <FilterChips spec={spec} />

      {spec.threads.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No matching correspondence right now.
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-border/40">
          {spec.threads.map((thread, index) => (
            <ThreadSection
              key={thread.conversation_id ?? `thread-${index}`}
              thread={thread}
              onReviewEmail={onReviewEmail}
            />
          ))}
        </div>
      )}

      {spec.graph_attachments && spec.graph_attachments.length > 0 && (
        <KaiGraphMailAttachmentsCard
          attachments={spec.graph_attachments}
          embedded
        />
      )}

      {spec.footnotes && spec.footnotes.length > 0 && (
        <ul className="space-y-1 text-xs text-muted-foreground">
          {spec.footnotes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
